import { Box, Divider, Stack, Typography, useTheme } from "@mui/material";
import { DataTable, SelectField, Tab, Tabs } from "components";
import {
  useGetAllCustomerUsers,
  useGetIntegrationMappedCustomers,
  useGetIntegrationMappedDevices,
  useGetNcentralDevices,
  useMapNcentralDevices,
} from "controller";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { emptyStringHandler, getFullName } from "utilz/helpers";
import { useDebounce } from "utilz/helpers/hooks/useDebounceHook";
import { NCENTRAL_SKELETON_ROWS } from "./constants";

export const NcentralStep3 = forwardRef(
  ({ onStepChange }: { onStepChange: (step: number) => void }, ref) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [selectedCustomer, setSelectedCustomer] = useState(0);

    const { data: devices, isLoading: isDevicesLoading } =
      useGetNcentralDevices();
    const {
      data: ncentralMappedCustomers,
      isLoading: isNcentralMappedCustomersLoading,
    } = useGetIntegrationMappedCustomers("ncentral");

    const {
      data: ncentralMappedDevices,
      isLoading: isNcentralMappedDevicesLoading,
    } = useGetIntegrationMappedDevices("ncentral");

    const { mutateAsync: mapDevice } = useMapNcentralDevices();

    const [transformedCustomerData, setTransformedCustomerData] = useState<
      any[]
    >([]);

    // The rows are built in the effect below, a render after the queries
    // settle. Keep the skeleton up until they exist.
    const isBuildingRows =
      Boolean(devices) &&
      Boolean(ncentralMappedCustomers?.length) &&
      Boolean(ncentralMappedDevices) &&
      transformedCustomerData.length === 0;

    // Debounced server-side search, as in Level, Datto and NinjaOne: users
    // beyond the loaded page stay reachable instead of filtering only what's
    // loaded.
    const [userSearch, setUserSearch] = useDebounce("", 300);
    const { data: customerUsers, isFetching: isCustomerUsersFetching } =
      useGetAllCustomerUsers({
        perPageCount: 2000,
        sortColumn: "first_name",
        sortDirection: "asc",
        ...(transformedCustomerData.length > 0 && {
          customerId:
            transformedCustomerData[selectedCustomer]?.deskday_customer_id,
        }),
        filter: [
          { column: "customer_users.status", operator: "=", value: "true" },
        ],
        searchQuery: userSearch,
      });

    useEffect(() => {
      if (devices && ncentralMappedCustomers && ncentralMappedDevices) {
        createMappingArray();
      }
    }, [devices, ncentralMappedCustomers, ncentralMappedDevices]);

    const createMappingArray = async () => {
      const groupedByCustomerID = devices?.reduce(
        (acc: Record<string, any>, obj: Record<string, any>) => {
          if (!acc[obj.customerId]) {
            acc[obj.customerId] = [];
          }
          const deviceIndex = ncentralMappedDevices?.findIndex(
            (device: Record<string, any>) =>
              device.device_id === `${obj.deviceId}`,
          );
          const data = {
            device_id: obj?.deviceId,
            device_name: obj?.longName,
            deskday_customer_user_id:
              deviceIndex !== -1
                ? ncentralMappedDevices[deviceIndex].deskday_customer_user_id
                : "",
            deskday_customer_user_name:
              deviceIndex !== -1
                ? ncentralMappedDevices[deviceIndex].deskday_customer_user_name
                : "",
            remote_control_uri: obj.remoteControlUri,
            meta_data: obj,
          };
          acc[obj.customerId].push(data);
          return acc;
        },
        {},
      );

      const dataArray: any = [];
      ncentralMappedCustomers?.map((customer: Record<string, any>) => {
        dataArray.push({
          mapping_id: customer.mapping_id,
          external_customer_id: Number(customer.external_customer_id),
          deskday_customer_id: customer.deskday_customer_id,
          devices:
            groupedByCustomerID &&
            groupedByCustomerID.hasOwnProperty(customer.external_customer_id)
              ? groupedByCustomerID[customer.external_customer_id]
              : [],
        });
      });

      setTransformedCustomerData(dataArray);
    };

    const handleUserChange = ({
      id,
      cellData,
      rowIndex,
    }: {
      id: string;
      cellData: Record<string, any>;
      rowIndex: number;
    }) => {
      if (!id) {
        setTransformedCustomerData((prev) => {
          const newPrev = [...prev];
          newPrev[selectedCustomer].devices[rowIndex].deskday_customer_user_id =
            "";
          newPrev[selectedCustomer].devices[
            rowIndex
          ].deskday_customer_user_name = "";
          return newPrev;
        });

        return;
      }
      const index = customerUsers?.result.findIndex(
        (user: Record<string, any>) => user.mapping_id === id,
      );

      if (index !== -1) {
        setTransformedCustomerData((prev) => {
          const fullname = getFullName(
            customerUsers?.result[index].first_name,
            customerUsers?.result[index].middle_name,
            customerUsers?.result[index].last_name,
          );
          const newPrev = [...prev];
          newPrev[selectedCustomer].devices[rowIndex].deskday_customer_user_id =
            customerUsers?.result[index].mapping_id;
          newPrev[selectedCustomer].devices[
            rowIndex
          ].deskday_customer_user_name = fullname;
          return newPrev;
        });
      }
    };

    const handleTransformForSubmit = async () => {
      const submitArray: Array<any> = [];
      transformedCustomerData?.map((customer: Record<string, any>) => {
        const devices = customer.devices.filter(
          (devices: Record<string, any>) =>
            devices.deskday_customer_user_id !== "" &&
            devices.deskday_customer_user_name !== "",
        );

        submitArray.push({
          ...customer,
          devices,
        });
      });

      return submitArray;
    };
    const cellBuilder = {
      user_name: (cellData: any, index?: number) => {
        return (
          <SelectField
            key={cellData.device_name}
            sx={{ width: "180px" }}
            name="customer"
            hideHelperBox
            options={customerUsers?.result || []}
            isSearchable
            isLoading={isCustomerUsersFetching}
            onSearchValueChange={setUserSearch}
            // The backend already filtered by `searchQuery`; don't re-filter.
            onFilterValue={() => true}
            value={cellData.deskday_customer_user_id}
            size="small"
            isResponsive={false}
            labelMappingKey="first_name"
            valueMappingKey="mapping_id"
            searchFieldProps={{
              placeholder: "Search user",
            }}
            menuItemBuilder={({ item, index }) => (
              <Box key={index}>
                {emptyStringHandler(item.first_name)}{" "}
                {emptyStringHandler(item.last_name)}
              </Box>
            )}
            onValueChange={(value) =>
              handleUserChange({ id: value, cellData, rowIndex: index || 0 })
            }
            isClearable
            // While a search narrows the list, a row's user may not be in it,
            // so fall back to the name held in the row.
            renderValue={(_selected, selectedOption) => {
              return selectedOption ? (
                <>
                  {emptyStringHandler(selectedOption?.first_name)}{" "}
                  {emptyStringHandler(selectedOption?.last_name)}
                </>
              ) : (
                emptyStringHandler(cellData.deskday_customer_user_name)
              );
            }}
          ></SelectField>
        );
      },
    };

    const onSubmit = async () => {
      const data = await handleTransformForSubmit();

      if (data.length > 0) {
        try {
          await mapDevice({ deviceMapping: data, source: "ncentral" });
          toast.success("Successfully mapped devices");
          return true;
        } catch (error) {
          toast.error("Failed to map devices");
          return false;
        }
      } else {
        return true;
      }
    };
    useImperativeHandle(ref, () => ({
      onSubmit,
    }));

    return (
      <Stack
        sx={{
          py: "24px",
          height: "calc(100% - 34px)",
          overflowY: "scroll",
        }}
        divider={
          <Divider
            flexItem
            sx={{
              marginTop: "24px",
            }}
          />
        }
      >
        <Stack sx={{ px: "32px", gap: "16px" }}>
          <Typography variant="h6">Device Mapping</Typography>
          <Typography variant="body-medium" color={theme.palette.lightBg.low}>
            Please map your users with corresponding device names in N-able
          </Typography>
        </Stack>

        <Stack
          direction={"row"}
          flex={1}
          sx={{
            width: "100%",
            overflow: "clip",
          }}
          divider={<Divider orientation="vertical" flexItem />}
        >
          <Box
            sx={{
              display: "block",
              overflowY: "scroll",
              height: "100%",
              width: "350px",
            }}
          >
            <Tabs
              orientation="vertical"
              value={selectedCustomer}
              onChange={(_, index) => {
                setSelectedCustomer(index);
              }}
            >
              {ncentralMappedCustomers?.map(
                (customer: Record<string, any>, index: number) => (
                  <Tab
                    key={customer.id}
                    label={customer.external_customer_name}
                  />
                ),
              )}
            </Tabs>
          </Box>
          <Box
            sx={{
              display: "block",
              overflowY: "scroll",
              height: "100%",
              flex: "1",
              padding: "32px 32px 0px 32px",
            }}
          >
            <DataTable
              isDataLoading={
                isDevicesLoading ||
                isNcentralMappedCustomersLoading ||
                isNcentralMappedDevicesLoading ||
                isBuildingRows
              }
              headers={header}
              data={transformedCustomerData[selectedCustomer]?.devices || []}
              uniqueKey="device_id"
              cellBuilders={cellBuilder}
              selectedItems={[]}
              currentPageNumber={0}
              itemsPerPage={NCENTRAL_SKELETON_ROWS}
              multiselect={false}
              containerStyles={{ height: "100%" }}
              headerCellStyles={{
                textTransform: "none",
              }}
            />
          </Box>
        </Stack>
      </Stack>
    );
  },
);

const users = [{ user_name: "User 1" }, { user_name: "User 2" }];
const header = [
  {
    name: "N-able Device",
    sortable: false,
    key: "device_name",
  },
  {
    name: "DeskDay User",
    sortable: true,
    key: "user_name",
  },
];
